// Mantém todos os clientes (desktop, mobile, PWA instalado, dispositivos/PDV)
// sempre na versão mais recente do sistema: detecta novo build e recarrega
// limpando caches antigos.
import { isPushWorker } from "./pushWorkerId";

const RELOAD_GUARD = "miahma-app-reloaded-at";
const VERSION_KEY = "miahma-app-build";

function currentBuildId(): string | null {
  try {
    const scripts = Array.from(document.querySelectorAll("script[src]")) as HTMLScriptElement[];
    const main = scripts.map((s) => s.getAttribute("src") || "").find((src) => /\/assets\/.*\.js/.test(src));
    return main || null;
  } catch {
    return null;
  }
}

async function purgeCaches() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      // Mantém só o worker de notificações da equipe (não guarda cache nem controla páginas).
      await Promise.all(regs.filter((r) => !isPushWorker(r)).map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
}

function hardReload() {
  // evita loop de reload (no máximo 1 a cada 60s)
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD) || 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
  } catch {
    /* ignore */
  }
  void purgeCaches().finally(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString(36));
    window.location.replace(url.toString());
  });
}

async function fetchLatestBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/?_ts=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(/src="([^"]*\/assets\/[^"]*\.js)"/);
    return match?.[1] || null;
  } catch {
    return null;
  }
}

async function checkForUpdate() {
  const current = currentBuildId();
  if (!current) return; // dev (src/main.tsx) — nada a fazer
  const latest = await fetchLatestBuildId();
  if (latest && latest !== current) {
    try {
      localStorage.setItem(VERSION_KEY, latest);
    } catch {
      /* ignore */
    }
    hardReload();
  }
}

export function initAppUpdater() {
  if (typeof window === "undefined") return;

  // Remove service workers/caches legados que podem servir uma versão antiga.
  void purgeCaches();

  // Recarrega quando um chunk antigo não existe mais no servidor.
  window.addEventListener("vite:preloadError", () => hardReload());
  window.addEventListener("error", (e) => {
    const msg = String((e as ErrorEvent)?.message || "");
    if (/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)) {
      hardReload();
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = String((e as PromiseRejectionEvent)?.reason?.message || "");
    if (/Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(msg)) {
      hardReload();
    }
  });

  // Verificação periódica + ao voltar para a aba (importante em PWA/celular e TVs/PDV).
  setTimeout(() => void checkForUpdate(), 1_500);
  setInterval(() => void checkForUpdate(), 60_000);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForUpdate();
  });
  window.addEventListener("focus", () => void checkForUpdate());
}
